import { useCallback, useEffect, useRef } from 'react'

import type {
  BodyScrollEvent,
  TooltipHideEvent,
  TooltipShowEvent,
} from 'ag-grid-community'
import { useRouter } from 'next/compat/router'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import {
  resetManifestEngaged,
  useManifestTableTracking,
} from '../../../hooks/useManifestTableTracking'
import {
  computeScrollDepth,
  getTooltipTarget,
  getTooltipTargetKey,
} from './manifestTrackingHelpers'

const COLUMN_SCROLL_DEBOUNCE_MS = 500

export const useManifestTracking = (
  listing: OrchestratedListingDetails | null
) => {
  const {
    onManifestColumnScrolled,
    onManifestTableEngaged,
    onManifestTableViewed,
    onManifestTooltip,
  } = useManifestTableTracking(listing)

  const containerRef = useRef<HTMLDivElement>(null)
  const viewedRef = useRef(false)
  const maxDepthRef = useRef(0)
  const firedDepthRef = useRef(0)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const router = useRouter()

  // Indirect through a ref so upstream churn in onManifestTableViewed doesn't
  // tear down the observer and drop a pending intersection callback.
  const onManifestTableViewedRef = useRef(onManifestTableViewed)
  useEffect(() => {
    onManifestTableViewedRef.current = onManifestTableViewed
  }, [onManifestTableViewed])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !viewedRef.current) {
          viewedRef.current = true
          onManifestTableViewedRef.current()
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!router) return
    router.events.on('routeChangeStart', resetManifestEngaged)
    return () => router.events.off('routeChangeStart', resetManifestEngaged)
  }, [router])

  const onBodyScroll = useCallback(
    (e: BodyScrollEvent) => {
      if (e.direction !== 'horizontal') return
      onManifestTableEngaged()

      const { columns: depth } = computeScrollDepth(e)
      if (depth > maxDepthRef.current) maxDepthRef.current = depth

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        if (maxDepthRef.current > firedDepthRef.current) {
          firedDepthRef.current = maxDepthRef.current
          onManifestColumnScrolled(maxDepthRef.current)
        }
      }, COLUMN_SCROLL_DEBOUNCE_MS)
    },
    [onManifestColumnScrolled, onManifestTableEngaged]
  )

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    },
    []
  )

  // ag-Grid can re-fire tooltipShow for a cell whose tooltip is already
  // showing (e.g. on a grid re-render, or a small pointer move within the
  // same cell) — this ref suppresses those repeats, and is cleared on
  // tooltipHide so the next genuine hover fires again.
  const lastTooltipTargetRef = useRef<string | null>(null)

  const onTooltipShow = useCallback(
    (e?: TooltipShowEvent) => {
      onManifestTableEngaged()
      const { column, rowIndex } = getTooltipTarget(e)
      const targetKey = getTooltipTargetKey(column, rowIndex)
      if (targetKey === lastTooltipTargetRef.current) return
      lastTooltipTargetRef.current = targetKey
      onManifestTooltip(column, rowIndex)
    },
    [onManifestTableEngaged, onManifestTooltip]
  )

  const onTooltipHide = useCallback((_e?: TooltipHideEvent) => {
    lastTooltipTargetRef.current = null
  }, [])

  return {
    containerRef,
    agGridProps: { onBodyScroll, onTooltipHide, onTooltipShow },
  }
}
